/**
 * Centralized sanitization helpers for user-controlled values that render
 * into the public Link Tree page. Defensive in depth — server-side RLS still
 * allows arbitrary text into block.data and theme JSONB, so the renderer is
 * the last line of defense against XSS / open-redirect / exfil channels.
 */

const SAFE_URL_PREFIX = /^(https?:|mailto:|tel:)/i;
const HASH_OR_RELATIVE = /^(#|\/|\?)/;

/** Returns the URL if it's safe to use as anchor href / window.open target,
 *  else null. Blocks: javascript:, data:, vbscript:, file:, etc. Allows
 *  http(s), mailto, tel. Treats bare hostnames ("example.com") as
 *  https://example.com — do NOT use this for iframe src; use
 *  `safeIframeSrc` instead. */
export function safeUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (SAFE_URL_PREFIX.test(trimmed)) return trimmed;
  if (HASH_OR_RELATIVE.test(trimmed)) return trimmed;
  // Bare domain like "example.com/path" — assume https. Only safe for
  // anchor href contexts (browser navigation), not for iframe src.
  if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/i.test(trimmed)) return `https://${trimmed}`;
  return null;
}

/** Stricter variant for iframe `src` — requires an explicit http/https
 *  protocol. Bare hostnames are rejected because a user-supplied "evil.com"
 *  rendered into an iframe should never be silently auto-promoted. */
export function safeIframeSrc(url: string | undefined | null): string | null {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return null;
}

const CSS_DENYLIST = /(javascript:|expression\s*\(|@import|behavior\s*:|vbscript:|<|>)/i;

/** Returns the CSS value if it's safe to drop into a style attribute, else
 *  returns a safe fallback. */
export function safeCss(value: string | undefined | null, fallback = ''): string {
  if (!value) return fallback;
  const trimmed = String(value).trim();
  if (!trimmed) return fallback;
  if (CSS_DENYLIST.test(trimmed)) return fallback;
  const urlMatches = trimmed.matchAll(/url\(([^)]*)\)/gi);
  for (const m of urlMatches) {
    const inner = m[1].replace(/['"]/g, '').trim();
    if (!/^https:\/\//i.test(inner)) return fallback;
  }
  return trimmed;
}

const PIXEL_ID_RULES: Record<string, RegExp> = {
  fb: /^\d{10,20}$/,
  tiktok: /^[A-Z0-9]{16,24}$/i,
  google: /^G-[A-Z0-9]{6,12}$/i,
  gtm: /^GTM-[A-Z0-9]{4,12}$/i,
};

export function isValidPixelId(provider: string, pixelId: string): boolean {
  const rule = PIXEL_ID_RULES[provider];
  if (!rule) return false;
  return rule.test(pixelId.trim());
}
